/**
 * `ifsc.lookup` — RBI IFSC directory lookup (powered by Razorpay's
 * public IFSC endpoint, the de-facto Indian standard).
 *
 * Used by:
 *  - `/bank-accounts/new` IFSC field → click "Fetch" to auto-fill bank
 *    name + branch on the KYC section.
 *  - `/vendors/$id/edit` (V1.0.x) — same auto-fill on the step-up
 *    change-bank-details flow.
 *
 * The lookup is a stateless read-only HTTP call to a public RBI-backed
 * directory, so it's a `publicProcedure` — no tenant context required.
 *
 * @see apps/api/src/adapters/ifsc/razorpay-ifsc.ts
 */
import { z } from "zod";
import { ifsc } from "../_common.js";

// ── inputs ──────────────────────────────────────────────────────

export const lookupIfscInput = z.object({
  ifsc,
});

// ── outputs ─────────────────────────────────────────────────────

export const lookupIfscOutput = z.object({
  ifsc: z.string(),
  bank: z.string(),
  branch: z.string(),
  address: z.string(),
  city: z.string(),
  district: z.string(),
  state: z.string(),
  micr: z.string().nullable(),
  swift: z.string().nullable(),
  contact: z.string().nullable(),
  supports: z.object({
    upi: z.boolean(),
    rtgs: z.boolean(),
    neft: z.boolean(),
    imps: z.boolean(),
  }),
});

// ── derived TS types ───────────────────────────────────────────
export type LookupIfscInput = z.infer<typeof lookupIfscInput>;
export type LookupIfscOutput = z.infer<typeof lookupIfscOutput>;
